import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function CrewPlacement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/e472d5fa03a42598e0115efbd691980a.jpeg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl mb-8 tracking-tight">
            Crew Placement
          </h1>
          <p className="text-xl md:text-2xl font-light tracking-wide">
            Professional crew solutions for exceptional yachts.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg md:text-xl leading-relaxed text-gray-900 mb-8">
            SYG Yachts offers dedicated crew placement services, connecting experienced, professional crew with world-class yachts. We understand that crew quality directly impacts onboard operations, owner satisfaction, and charter performance.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-gray-900">
            Our approach emphasizes compatibility, professionalism, and long-term success.
          </p>
        </div>
      </section>

      {/* Scope of Services */}
      <section className="py-32 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl mb-20 text-center">
            Scope of Services
          </h2>
          
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 max-w-4xl mx-auto">
            <div>
              <h3 className="text-xl font-medium mb-3">Crew recruitment and screening</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive vetting process to identify qualified candidates
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Captain, officer, and senior crew placement</h3>
              <p className="text-gray-600 leading-relaxed">
                Strategic placement of key leadership positions
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Temporary and permanent crew solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                Flexible staffing options for varying operational needs
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Reference and background verification</h3>
              <p className="text-gray-600 leading-relaxed">
                Thorough due diligence on all candidates
              </p>
            </div>
            
            <div className="md:col-span-2">
              <h3 className="text-xl font-medium mb-3">Alignment with yacht program and ownership style</h3>
              <p className="text-gray-600 leading-relaxed">
                Ensuring crew fit matches operational requirements and owner expectations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl mb-12 text-center">
            Our Philosophy
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-gray-900 text-center">
            Successful placements are built on understanding both the yacht and the people onboard. We prioritize discretion, integrity, and fit in every engagement.
          </p>
        </div>
      </section>

      {/* Crew Application Form Section */}
      <section className="relative py-24 flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/56d73fd1c84c20ea8d702bbd12b87e8f.jpeg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-2xl mx-auto px-6">
          <div className="text-center text-white mb-10">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight">
              Join Our Crew Network
            </h2>
            <p className="text-lg md:text-xl font-light">
              Submit your details and CV to be considered for positions on world-class superyachts.
            </p>
          </div>
          
          <form 
            id="crew-application-form"
            data-readdy-form
            className="bg-white/95 backdrop-blur-sm p-8 md:p-10"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Handle CV file - show as "Uncollectable" for file uploads
              const cvFile = formData.get('cv') as File;
              if (cvFile && cvFile.name) {
                formData.set('cv', 'Uncollectable');
              }
              
              try {
                const response = await fetch('https://readdy.ai/api/form/d6334m2bdoed7v0v8jvg', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                });
                
                if (response.ok) {
                  alert('Thank you! Your application has been submitted successfully.');
                  form.reset();
                } else {
                  alert('There was an error submitting your application. Please try again.');
                }
              } catch (error) {
                alert('There was an error submitting your application. Please try again.');
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input 
                  type="text" 
                  name="fullName"
                  required
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input 
                  type="tel" 
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
                <input 
                  type="text" 
                  name="nationality"
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="Your nationality"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position Sought *</label>
                <select 
                  name="position"
                  required
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer bg-white"
                >
                  <option value="">Select position</option>
                  <option value="Captain">Captain</option>
                  <option value="First Officer">First Officer</option>
                  <option value="Chief Engineer">Chief Engineer</option>
                  <option value="Second Engineer">Second Engineer</option>
                  <option value="ETO">ETO</option>
                  <option value="Bosun">Bosun</option>
                  <option value="Deckhand">Deckhand</option>
                  <option value="Chief Stewardess">Chief Stewardess</option>
                  <option value="Second Stewardess">Second Stewardess</option>
                  <option value="Stewardess">Stewardess</option>
                  <option value="Head Chef">Head Chef</option>
                  <option value="Sous Chef">Sous Chef</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
                <select 
                  name="experience"
                  required
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer bg-white"
                >
                  <option value="">Select experience</option>
                  <option value="0-1 years">0-1 years</option>
                  <option value="1-3 years">1-3 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5-10 years">5-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Certifications & Qualifications</label>
              <input 
                type="text" 
                name="certifications"
                className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors"
                placeholder="e.g., STCW, ENG1, Yachtmaster, etc."
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload CV (PDF preferred)</label>
              <input 
                type="file" 
                name="cv"
                accept=".pdf,.doc,.docx"
                className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-black file:text-white file:cursor-pointer hover:file:bg-gray-800"
              />
              <p className="text-xs text-gray-500 mt-2">Please email your CV directly to us for review</p>
            </div>
            
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
              <textarea 
                name="additionalInfo"
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-black transition-colors resize-none"
                placeholder="Tell us about your experience, availability, and any other relevant details..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>
            
            <button 
              type="submit"
              className="w-full bg-black text-white py-4 text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Submit Application
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}